import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tabs, Descriptions, Avatar, Badge, Button, Space, Card, Typography, Tag, Table } from "antd";
import * as Icon from "react-feather";
import { useQuery } from "react-query";
import { api_srv } from "services";
import { show_toast } from "utils/helpers";
import { ModalContext } from "context/ModalContext";
import AddProductUnderwriter from "components/widgets/modals/AddProductUnderwriter";
import { fetchUnderwriterDetails } from "services/apiSrv";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function View_Product_Underwriter() {
    const { id } = useParams();
    const [underwriterData, setUnderwriterData] = useState(null);
    const { setModalShow, modalClose } = useContext(ModalContext);
    const [editModalVisible, setEditModalVisible] = useState(false);

    // Fetch underwriter details
    const get_underwriter_details = async () => {
        // Placeholder: Replace with actual fetch_underwriter_by_id when available
        let resp = await fetchUnderwriterDetails(id);
        console.log("REST", resp)
    
        return resp;
    };

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['underwriter_details', id],
        queryFn: get_underwriter_details,
        onSuccess: (data) => {
            if (data?.data) {
                setUnderwriterData({
                    underwriterSystemRef: data.data.underwriterSystemRef || data.data.id || id,
                    name: data.data.intermediaryNames || data.data.name || data.data.underwriterName || data.data.companyName || "N/A",
                    remarks: data.data.kycValidationremarks || data.data.remarks || "No remarks",
                    emails: data.data.emails || [],
                    allowedToCreateProduct: data.data.allowedToCreateProduct || "no",
                    insuranceClasses: data.data.insuranceClasses || { motor: false, commercial: false },
                    channelType: data.data.channelType || "manual",
                    channelDetails: data.data.channelDetails || [],
                    settlementType: data.data.settlementType || "MPESANUMBER",
                    mpesaNumber: data.data.mpesaNumber || "",
                    paybillNumber: data.data.paybillNumber || "",
                    accountNumber: data.data.accountNumber || "",
                    bankName: data.data.bankName || "",
                    branch: data.data.branch || "",
                    settlementFrequency: data.data.settlementFrequency || "EOD",
                    minimumSettlement: data.data.minimumSettlement || 0,
                    threshold: data.data.threshold || 0,
                    status: data.data.status || "UNKNOWN",
                    createdAt: data.data.createdAt || new Date().toISOString(),
                });
            }
        },
        onError: (err) => {
            show_toast(`Failed to fetch underwriter: ${err.message}`, 'error');
        },
    });

    // Status color mapping
    const getStatusColor = (status) => {
        const statusColors = {
            active: '#52c41a',
            inactive: '#ff4d4f',
            pending: '#faad14',
            suspended: '#ff4d4f',
        };
        return statusColors[status?.toLowerCase()] || '#52c41a';
    };

    // Action handlers
    const handleEdit = () => {
        setEditModalVisible(true);
        setModalShow(true);
    };

    const handleSendEmail = async () => {
        try {
            console.log("Sending email to underwriter:", id);
            show_toast('Email sent successfully', 'success');
        } catch (error) {
            show_toast('Failed to send email!', 'error');
        }
    };

    const handleDelete = async () => {
        try {
            console.log("Deleting underwriter:", id);
            show_toast('Underwriter deleted successfully', 'success');
            modalClose();
        } catch (error) {
            show_toast('Failed to delete underwriter!', 'error');
        }
    };

    // Handle update submission
    const handleUpdateUnderwriter = async (updatedData) => {
        try {
            await (await api_srv).update_underwriter(id, updatedData);
            show_toast('Underwriter updated successfully', 'success');
            refetch(); // Refresh underwriter data
            setEditModalVisible(false);
            modalClose();
        } catch (error) {
            show_toast(`Failed to update underwriter: ${error.message}`, 'error');
        }
    };

    // Table configuration for channel details
    const channelDetailsColumns = [
        { title: "Key", dataIndex: "key", key: "key" },
        { title: "Value", dataIndex: "value", key: "value" },
    ];

    if (isLoading) return <div>Loading...</div>;
    if (error || !underwriterData) return <div>Error: {error?.message || "No underwriter data found"}</div>;
    

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
            {/* Header Section */}
            <Card
                style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none' }}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>
                                Product Underwriter
                            </Text>
                            <Title level={3} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>
                                {underwriterData?.name || 'N/A'}
                            </Title>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Badge
                                color={getStatusColor(underwriterData?.status)}
                                text={underwriterData?.status?.toUpperCase() || 'UNKNOWN'}
                                style={{ fontSize: '12px', fontWeight: '600' }}
                            />
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<Icon.Edit size={16} />}
                                    onClick={handleEdit}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type="default"
                                    icon={<Icon.Mail size={16} />}
                                    onClick={handleSendEmail}
                                >
                                    Send Email
                                </Button>
                                <Button
                                    type="default"
                                    icon={<Icon.Trash size={16} />}
                                    onClick={handleDelete}
                                    danger
                                >
                                    Delete
                                </Button>
                            </Space>
                        </div>
                    </div>
                }
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
                            Underwriter Details
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                            Underwriter ID: {underwriterData?.underwriterSystemRef || 'N/A'}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                            Created: {underwriterData?.createdAt ? new Date(underwriterData.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Edit Modal */}
            {editModalVisible && (
                <AddProductUnderwriter
                    fetchUnderwriters={refetch}
                    show={editModalVisible}
                    onHide={() => {
                        setEditModalVisible(false);
                        modalClose();
                    }}
                    initialData={underwriterData}
                    editMode={true}
                    onSubmit={handleUpdateUnderwriter}
                />
            )}

            {/* Tabs Section */}
            <Tabs defaultActiveKey="1" size="large" style={{ marginBottom: '24px' }}>
                {/* Basic Information Tab */}
                <TabPane tab={<Space><Icon.FileText size={16} /> Basic Information</Space>} key="1">
                    <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Descriptions
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon.User size={18} style={{ color: '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>Underwriter Information</span>
                                </div>
                            }
                            bordered
                            column={2}
                            size="small"
                            style={{ background: '#fff', borderRadius: '8px' }}
                        >
                            <Descriptions.Item label="Underwriter Name">
                                <Tag color="cyan">{underwriterData?.name || 'N/A'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Underwriter ID">
                                <span>{underwriterData?.underwriterSystemRef || 'N/A'}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Remarks" span={2}>
                                <Text style={{ lineHeight: '1.6' }}>{underwriterData?.remarks || 'No remarks available'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Email Addresses" span={2}>
                                {underwriterData?.emails?.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {underwriterData.emails.map((email, index) => (
                                            <Tag key={index} color="blue" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Icon.Mail size={14} />
                                                <a href={`mailto:${email}`}>{email}</a>
                                            </Tag>
                                        ))}
                                    </div>
                                ) : (
                                    <Text type="secondary">No email addresses provided</Text>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </TabPane>

                {/* Permissions & Settings Tab */}
                <TabPane tab={<Space><Icon.CheckCircle size={16} /> Permissions & Settings</Space>} key="2">
                    <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Descriptions
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon.Shield size={18} style={{ color: '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>Permissions & Settings</span>
                                </div>
                            }
                            bordered
                            column={2}
                            size="small"
                            style={{ background: '#fff', borderRadius: '8px' }}
                        >
                            <Descriptions.Item label="Allowed to Create Product">
                                <Badge
                                    color={underwriterData?.allowedToCreateProduct === "yes" ? "green" : "red"}
                                    text={underwriterData?.allowedToCreateProduct === "yes" ? "Yes" : "No"}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Insurance Classes" span={2}>
                                {underwriterData?.insuranceClasses && Object.values(underwriterData.insuranceClasses).some(val => val) ? (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {underwriterData.insuranceClasses.motor && <Tag color="purple">Motor</Tag>}
                                        {underwriterData.insuranceClasses.commercial && <Tag color="cyan">Commercial</Tag>}
                                    </div>
                                ) : (
                                    <Text type="secondary">No insurance classes assigned</Text>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </TabPane>

                {/* Channel Configuration Tab */}
                <TabPane tab={<Space><Icon.Settings size={16} /> Channel Configuration</Space>} key="3">
                    <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Descriptions
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon.Layers size={18} style={{ color: '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>Channel Configuration</span>
                                </div>
                            }
                            bordered
                            column={1}
                            size="small"
                            style={{ background: '#fff', borderRadius: '8px' }}
                        >
                            <Descriptions.Item label="Channel Type">
                                <Badge color="blue" text={underwriterData?.channelType?.toUpperCase() || 'N/A'} />
                            </Descriptions.Item>
                        </Descriptions>
                        <div style={{ marginTop: '24px' }}>
                            <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Channel Details</Title>
                            {underwriterData?.channelDetails?.length > 0 ? (
                                <Table
                                    columns={channelDetailsColumns}
                                    dataSource={underwriterData.channelDetails}
                                    rowKey="key"
                                    size="small"
                                    style={{ marginTop: '16px' }}
                                    pagination={false}
                                />
                            ) : (
                                <div className="text-center py-5">
                                    <Icon.Info size={48} className="text-muted mb-3" />
                                    <h6 className="text-muted">No channel details available</h6>
                                </div>
                            )}
                        </div>
                    </Card>
                </TabPane>

                {/* Settlement Configuration Tab */}
                <TabPane tab={<Space><Icon.DollarSign size={16} /> Settlement Configuration</Space>} key="4">
                    <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Descriptions
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon.DollarSign size={18} style={{ color: '#1890ff' }} />
                                    <span style={{ fontWeight: 600 }}>Settlement Information</span>
                                </div>
                            }
                            bordered
                            column={2}
                            size="small"
                            style={{ background: '#fff', borderRadius: '8px' }}
                        >
                            <Descriptions.Item label="Settlement Type">
                                <Badge color="green" text={underwriterData?.settlementType?.toUpperCase() || 'N/A'} />
                            </Descriptions.Item>
                            <Descriptions.Item label="Settlement Frequency">
                                <Badge
                                    color={underwriterData?.settlementFrequency === "THRESHOLD" ? "purple" : "cyan"}
                                    text={underwriterData?.settlementFrequency?.toUpperCase() || 'N/A'}
                                />
                            </Descriptions.Item>
                            {underwriterData?.settlementType === "MPESANUMBER" && (
                                <Descriptions.Item label="M-Pesa Number">
                                    <span>{underwriterData?.mpesaNumber || 'N/A'}</span>
                                </Descriptions.Item>
                            )}
                            {underwriterData?.settlementType === "PAYBILL" && (
                                <>
                                    <Descriptions.Item label="Paybill Number">
                                        <span>{underwriterData?.paybillNumber || 'N/A'}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Account Number">
                                        <span>{underwriterData?.accountNumber || 'N/A'}</span>
                                    </Descriptions.Item>
                                </>
                            )}
                            {underwriterData?.settlementType === "BANK" && (
                                <>
                                    <Descriptions.Item label="Bank Name">
                                        <span>{underwriterData?.bankName || 'N/A'}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Account Number">
                                        <span>{underwriterData?.accountNumber || 'N/A'}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Branch">
                                        <span>{underwriterData?.branch || 'N/A'}</span>
                                    </Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label="Minimum Settlement">
                                <span>{underwriterData?.minimumSettlement?.toLocaleString() || 'N/A'}</span>
                            </Descriptions.Item>
                            {underwriterData?.settlementFrequency === "THRESHOLD" && (
                                <Descriptions.Item label="Threshold Amount">
                                    <span>{underwriterData?.threshold?.toLocaleString() || 'N/A'}</span>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
}